import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import i18n from "../../../../i18n/i18n";
import {
    Plus, FileText, Upload, X, Receipt, Loader,
    Briefcase, Info, Eye, AlertCircle, XCircle, Edit,
} from "lucide-react";
import Swal from "sweetalert2";
import {
    getMyNoteDeFraisRequestsRequest,
    createNoteDeFraisRequestRequest,
    cancelNoteDeFraisRequestRequest,
    updateNoteDeFraisRequestRequest,
} from "../../../../redux/actions/noteDeFraisRequestActions";
import { fetchNoteDeFraisRequest } from "../../../../redux/actions/noteDeFraisActions";
import { fetchOrdreMissionActifsRequest } from "../../../../redux/actions/ordreMissionActions";
import { getMyMissionRequestsRequest } from "../../../../redux/actions/missionRequestActions";
import "../../parametres/parametresStyles/ParamConges.css";
import "../MesrequetesStyles/MesOrdresShared.css";
import useRequestHistoryAutoRefresh from "./useRequestHistoryAutoRefresh";
import { getRejectionReason, getRequestTracking } from "./requestWorkflowTracking";

/* ─── helpers de style partagés ─────────────────────────── */
const btn = (extra = {}) => ({
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "7px 14px", borderRadius: 8, fontSize: 12,
    fontWeight: 600, cursor: "pointer", transition: "all .15s",
    ...extra,
});

const CATEGORY_COLOR = {
    TRANSPORT:     { text: "#1d4ed8", bg: "#dbeafe", border: "#93c5fd" },
    HEBERGEMENT:   { text: "#6d28d9", bg: "#ede9fe", border: "#c4b5fd" },
    RESTAURATION:  { text: "#065f46", bg: "#d1fae5", border: "#6ee7b7" },
    AUTRE:         { text: "#92400e", bg: "#fef3c7", border: "#fcd34d" },
};

const STATUS_CONFIG = {
    PENDING_MANAGER:  { label: "En attente du manager", dot: "#f59e0b", color: "#b45309", bg: "#fef3c7", border: "#fcd34d" },
    PENDING_ADMIN:    { label: "En attente de l'admin RH", dot: "#f59e0b", color: "#b45309", bg: "#fef3c7", border: "#fcd34d" },
    APPROVED:         { label: "Approuvée",          dot: "#16a34a", color: "#166534", bg: "#dcfce7", border: "#86efac" },
    REJECTED:         { label: "Rejetée",            dot: "#ef4444", color: "#991b1b", bg: "#fee2e2", border: "#fca5a5" },
    PAID:             { label: "Remboursé",          dot: "#059669", color: "#065f46", bg: "#d1fae5", border: "#6ee7b7" },
    CANCELLED:        { label: "Annulée",            dot: "#9ca3af", color: "#374151", bg: "#f3f4f6", border: "#d1d5db" },
};

const StatutBadge = ({ statut, demande }) => {
    if (statut === "APPROVED" && demande?.statutRemboursement === "EN_ATTENTE") {
        return (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, color: "#075985", background: "#e0f2fe", border: "1px solid #7dd3fc", whiteSpace: "nowrap" }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#0ea5e9", flexShrink: 0 }} />
                {tr("Approuvée")}
            </span>
        );
    }
    const c = STATUS_CONFIG[statut] || { label: statut, dot: "#9ca3af", color: "#374151", bg: "#f3f4f6", border: "#d1d5db" };
    return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, color: c.color, background: c.bg, border: `1px solid ${c.border}`, whiteSpace: "nowrap" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.dot, flexShrink: 0 }} />
            {tr(c.label)}
        </span>
    );
};

const CategoryChip = ({ cat }) => {
    const categories = String(cat || "-").split(", ");
    return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
            {categories.map(category => {
                const c = CATEGORY_COLOR[category] || { text: "#374151", bg: "#f3f4f6", border: "#d1d5db" };
                return <span key={category} style={{ display: "inline-flex", alignItems: "center", padding: "2px 8px", borderRadius: 5, fontSize: 11, fontWeight: 700, color: c.text, background: c.bg, border: `1px solid ${c.border}` }}>{tr(category)}</span>;
            })}
        </span>
    );
};
const tr = (key, options = {}) => i18n.t(key, { ...options, defaultValue: key });

const FieldBlock = ({ label, children }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".05em" }}>{label}</label>
        {children}
    </div>
);

const ReadBox = ({ children }) => (
    <div style={{ whiteSpace: "pre-wrap", padding: "10px 12px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12, color: "#374151", lineHeight: 1.6 }}>
        {children}
    </div>
);

/* ─── composant principal ────────────────────────────────── */
const ExpenseManagement = () => {
    const { t, i18n: currentI18n } = useTranslation();
    const dispatch = useDispatch();

    const { myRequests, loading } = useSelector((s) => s.noteDeFraisRequests || { myRequests: [], loading: false });
    const { notesDeFrais }        = useSelector((s) => s.noteDeFrais        || { notesDeFrais: [] });
    const { ordresMissionActifs } = useSelector((s) => s.ordreMission       || { ordresMissionActifs: [] });
    const { myRequests: myMissionRequests } = useSelector((s) => s.missionRequests || { myRequests: [] });

    const canCancelRequest = (request) => {
        const status = String(request?.statusRaw || request?.status || "").toUpperCase();
        return (status === "PENDING_MANAGER" || status === "PENDING_ADMIN")
            && request?.managerDecision == null
            && request?.adminDecision == null;
    };

    const handleCancelRequest = (request) => {
        Swal.fire({
            icon: "warning",
            title: t("Annuler la note de frais ?"),
            text: t("Cette action est possible uniquement avant la première validation du workflow."),
            showCancelButton: true,
            confirmButtonColor: "#dc2626",
            cancelButtonColor: "#64748b",
            confirmButtonText: t("Oui, annuler"),
            cancelButtonText: t("Retour"),
        }).then((result) => {
            if (result.isConfirmed) dispatch(cancelNoteDeFraisRequestRequest(request.id));
        });
    };

    const [showModal,          setShowModal]          = useState(false);
    const [isEditMode,         setIsEditMode]         = useState(false);
    const [currentRequestId,   setCurrentRequestId]   = useState(null);
    const [showDetailsModal,   setShowDetailsModal]   = useState(false);
    const [showMissionModal,   setShowMissionModal]   = useState(false);
    const [selectedDemande,    setSelectedDemande]    = useState(null);

    const handleCloseModal = () => {
        setShowModal(false);
        setIsEditMode(false);
        setCurrentRequestId(null);
        setForm({ ordreMissionId: "", noteDeFraisIds: [], items: [], category: "", dateDepense: "", montant: "", avance: "", justificatifAvance: null, description: "", centreCouts: "", urgence: false, justificatifs: [] });
    };

    const handleEditClick = (request) => {
        setForm({
            ordreMissionId: request.ordreMissionId || "",
            noteDeFraisIds: [String(request.noteDeFrais?.id || request.noteDeFraisId || "")].filter(Boolean),
            category: request.category || "",
            dateDepense: request.dateDepense ? new Date(request.dateDepense).toISOString().split('T')[0] : "",
            montant: request.montantDepense || "",
            avance: request.avance ?? "",
            justificatifAvance: request.justificatifAvance || null,
            description: request.description || "",
            centreCouts: request.centreCouts || "",
            urgence: request.urgent || false,
            justificatifs: request.justificatifs || [],
        });
        setIsEditMode(true);
        setCurrentRequestId(request.id);
        setShowModal(true);
    };

    const [form, setForm] = useState({
        ordreMissionId: "", noteDeFraisIds: [], items: [], category: "",
        dateDepense: "", montant: "", avance: "", justificatifAvance: null, description: "",
        centreCouts: "", urgence: false, justificatifs: [],
    });

    useEffect(() => {
        dispatch(fetchNoteDeFraisRequest());
        dispatch(getMyNoteDeFraisRequestsRequest());
        dispatch(fetchOrdreMissionActifsRequest());
        dispatch(getMyMissionRequestsRequest());
    }, [dispatch]);

    useRequestHistoryAutoRefresh(() => dispatch(getMyNoteDeFraisRequestsRequest({ silent: true })));

    const formatDate = (d) => d ? new Date(d).toLocaleDateString(currentI18n.language, { day: "2-digit", month: "short", year: "numeric" }) : t("N/A");
    const formatMontant = (v) => (v == null || isNaN(v)) ? "0" : new Intl.NumberFormat(currentI18n.language === "ar" ? "ar-MA" : currentI18n.language === "en" ? "en-MA" : "fr-MA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

    const getRequestCode     = (d) => d?.code     || d?.noteDeFrais?.code     || "";

    const myApprovedMissions = useMemo(() => (myMissionRequests || [])
        .filter(r => r.status === "APPROVED" && r.actif !== false && r.ordreMissionActif !== false && r.statut !== "INACTIF"), 
    [myMissionRequests]);

    const selectedMissionRequest = useMemo(() =>
            form.ordreMissionId ? myApprovedMissions.find(r => r.ordreMissionId === Number(form.ordreMissionId)) || null : null,
        [form.ordreMissionId, myApprovedMissions]);

    const selectedOM = useMemo(() =>
            form.ordreMissionId
                ? ordresMissionActifs.find(om => om.id === Number(form.ordreMissionId)) || selectedMissionRequest || null
                : null,
        [form.ordreMissionId, ordresMissionActifs, selectedMissionRequest]);

    const getMissionField = (mission, ordreKey, requestKey) => mission?.[ordreKey] || mission?.[requestKey] || "";
    const getMissionDateDebut = (mission) => getMissionField(mission, "dateDebut", "ordreMissionDateDebut");
    const getMissionDateFin = (mission) => getMissionField(mission, "dateFin", "ordreMissionDateFin");
    const getMissionLieu = (mission) =>
        getMissionField(mission, "lieu", "ordreMissionLieu") ||
        getMissionField(mission, "ville", "ordreMissionVille") ||
        getMissionField(mission, "adresse", "ordreMissionAdresse");

    const isItemActif = (item) => item && item.actif !== false && item.actif !== "false" && item.statut !== "INACTIF" && item.isActif !== false;

    const typesDisponibles = useMemo(() => {
        const rawList = selectedOM ? (selectedOM.notesDeFraisAutorisees || []) : notesDeFrais;
        return (rawList || []).filter(isItemActif);
    }, [selectedOM, notesDeFrais]);

    const maxDate = useMemo(() => {
        const today = new Date().toISOString().split("T")[0];
        const dateFin = getMissionDateFin(selectedOM);
        return dateFin && dateFin < today ? dateFin : today;
    }, [selectedOM]);

    const getRequestCategory = (d) => {
        const categories = [...new Set((d?.items || []).map(item => item.category).filter(Boolean))];
        return categories.length > 0 ? categories.join(", ") : d?.category || d?.noteDeFrais?.category || d?.requestType || "-";
    };

    const handleOMChange = (e) => {
        const id = e.target.value;
        const mission = ordresMissionActifs.find(om => om.id === Number(id)) ||
            myApprovedMissions.find(r => r.ordreMissionId === Number(id));
        const dateDebut = getMissionDateDebut(mission);
        const dateFin = getMissionDateFin(mission);
        setForm(p => ({
            ...p, ordreMissionId: id, noteDeFraisIds: [], items: [], category: "",
            centreCouts: mission?.centreCout || mission?.ordreMissionCentreCout || p.centreCouts,
            dateDepense: mission && dateDebut && (!p.dateDepense || p.dateDepense < dateDebut || p.dateDepense > dateFin)
                ? dateDebut : p.dateDepense,
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const selectedTypes = typesDisponibles.filter(t => form.noteDeFraisIds.includes(String(t.id)));
        if (selectedTypes.length === 0) return Swal.fire({ icon: "error", title: t("Erreur"), text: t("Veuillez sélectionner au moins un type de frais.") });
        const items = isEditMode ? [{ noteDeFraisId: selectedTypes[0]?.id, dateDepense: form.dateDepense, montantDepense: form.montant, description: form.description, devise: selectedTypes[0]?.devise || "MAD" }] : form.items;
        if (items.length !== selectedTypes.length) return Swal.fire({ icon: "error", title: t("Informations manquantes"), text: t("Renseignez chaque catégorie sélectionnée.") });
        if (Number(form.avance) > 0 && !form.justificatifAvance)
            return Swal.fire({ icon: "error", title: t("Justificatif requis"), text: t("Ajoutez le justificatif de l'avance reçue des RH.") });
        for (const item of items) {
            const type = selectedTypes.find(t => t.id === Number(item.noteDeFraisId));
            const montant = Number(item.montantDepense);
            if (!montant || !item.dateDepense || !item.description?.trim())
                return Swal.fire({ icon: "error", title: t("Informations manquantes"), text: t("Renseignez le montant, la date et la description pour {{category}}.", { category: t(type.category, { defaultValue: type.category }) }) });
            if (type.montantMinimum && montant < type.montantMinimum)
                return Swal.fire({ icon: "error", title: t("Montant trop faible"), text: `${t(type.category, { defaultValue: type.category })} : ${t("minimum")} ${formatMontant(type.montantMinimum)} ${type.devise || "MAD"}` });
            if (type.montantMaximum && montant > type.montantMaximum)
                return Swal.fire({ icon: "error", title: t("Montant trop élevé"), text: `${t(type.category, { defaultValue: type.category })} : ${t("maximum")} ${formatMontant(type.montantMaximum)} ${type.devise || "MAD"}` });
            if (type.obligatoire && form.justificatifs.length === 0)
                return Swal.fire({ icon: "error", title: t("Justificatif requis"), text: t("{{category}} exige un justificatif ({{document}}).", { category: t(type.category, { defaultValue: type.category }), document: type.typeJustificatif || t("requis") }) });
        }

        const payload = {
            noteDeFraisIds: selectedTypes.map(type => type.id), items,
            ordreMissionId: form.ordreMissionId ? Number(form.ordreMissionId) : null,
            dateDepense: items[0].dateDepense, montantDepense: Number(items[0].montantDepense),
            avance: form.avance === "" ? 0 : Number(form.avance),
            justificatifAvance: form.justificatifAvance,
            description: items[0].description, centreCouts: form.centreCouts || null,
            urgent: form.urgence,
            justificatifs: form.justificatifs, devise: selectedTypes[0].devise || "MAD",
        };

        if (isEditMode && currentRequestId) {
            const { noteDeFraisIds, ...singleRequestPayload } = payload;
            dispatch(updateNoteDeFraisRequestRequest(currentRequestId, { ...singleRequestPayload, noteDeFraisId: noteDeFraisIds[0] }));
        } else {
            dispatch(createNoteDeFraisRequestRequest(payload));
        }
        handleCloseModal();
    };

    const selectedTypes = typesDisponibles.filter(t => form.noteDeFraisIds.includes(String(t.id)));
    const selectedType = selectedTypes[0];
    const totalFrais = isEditMode
        ? Number(form.montant) || 0
        : form.items.reduce((total, item) => total + (Number(item.montantDepense) || 0), 0);
    const soldeAvance = (Number(form.avance) || 0) - totalFrais;
    const getSoldeAvance = (demande) => Number(demande?.soldeAvance ?? ((Number(demande?.avance) || 0) - (Number(demande?.montantDepense) || 0)));
    const getLibelleSolde = (solde) => solde > 0 ? t("À rendre aux RH") : solde < 0 ? t("À recevoir des RH") : t("Avance soldée");
    const updateItem = (noteDeFraisId, patch) => setForm(p => ({
        ...p,
        items: p.items.map(item => String(item.noteDeFraisId) === String(noteDeFraisId) ? { ...item, ...patch } : item),
    }));

    return (
        <div className="pcPage mesOrdresStyle mesOrdresExpense">

            {/* ── Header ── */}
            <div style={{ margin: "0 18px 10px 18px", padding: "18px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20, borderBottom: "1px solid #f1f5f9" }}>
                <div>
                    <h1 style={{ margin: 0, color: "var(--admin-primary)", fontSize: 19, fontWeight: 700, lineHeight: 1.2 }}>{t("Mes notes de frais")}</h1>
                    <p style={{ margin: "5px 0 0", color: "#94a3b8", fontSize: 12 }}>{t("Déclarez vos frais professionnels et consultez vos remboursements.")}</p>
                </div>
                <button
                    onClick={() => setShowModal(true)}
                    style={{ ...btn({ padding: "9px 16px", border: "none", background: "var(--admin-accent)", color: "#fff", fontSize: 13, boxShadow: "0 1px 3px rgba(0,0,0,.12)", whiteSpace: "nowrap", flexShrink: 0 }) }}
                    onMouseEnter={e => e.currentTarget.style.filter = "brightness(1.08)"}
                    onMouseLeave={e => e.currentTarget.style.filter = "brightness(1)"}
                >
                    <Plus size={15} /> {t("Nouvelle demande")}
                </button>
            </div>

            {/* ── Card table ── */}
            <div style={{ padding: "0 18px 28px" }}>
                <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, boxShadow: "0 1px 3px rgba(16,24,40,.05)", overflow: "hidden" }}>
                    <div style={{ padding: "12px 16px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <h2 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "var(--admin-primary)" }}>{t("Historique des déclarations")}</h2>
                        {myRequests.length > 0 && (
                            <span style={{ padding: "2px 9px", borderRadius: 999, background: "#f0f4ff", color: "var(--admin-accent)", fontSize: 11, fontWeight: 700, border: "1px solid #dde6fb" }}>
                                {myRequests.length} {t(myRequests.length > 1 ? "déclarations" : "déclaration")}
                            </span>
                        )}
                    </div>
                    <div style={{ padding: 14 }}>
                        {loading && myRequests.length === 0 ? (
                            <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
                                <Loader size={28} className="spin" style={{ color: "var(--admin-accent)", opacity: .7 }} />
                            </div>
                        ) : myRequests.length === 0 ? (
                            <div style={{ padding: "40px 20px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 10, color: "#94a3b8" }}>
                                <div style={{ width: 48, height: 48, borderRadius: 12, background: "#f8fafc", border: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <Receipt size={22} style={{ opacity: .5 }} />
                                </div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: "#6b7280" }}>{t("Aucune demande de note de frais.")}</div>
                                <div style={{ fontSize: 12, color: "#94a3b8" }}>{t("Cliquez sur « Nouvelle demande » pour commencer.")}</div>
                            </div>
                        ) : (
                            <div style={{ width: "100%", overflowX: "auto", borderRadius: 10, border: "1px solid #eef1f5" }}>
                                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: 900 }}>
                                    <thead>
                                    <tr>
                                        {["Catégorie & date", "Total frais", "Avance / solde", "Devise", "Ordre de mission lié", "Statut", "Suivi", ""].map((h, i) => (
                                            <th key={i} style={{ background: "#f8f9fa", color: "#6b7280", fontSize: 11, fontWeight: 700, textAlign: i === 7 ? "right" : "left", padding: "10px 12px", borderBottom: "1px solid #e5e7eb", textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap" }}>{t(h)}</th>
                                        ))}
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {myRequests.map((d, idx) => (
                                        <tr key={d.id} style={{ background: idx % 2 === 0 ? "#fff" : "#fcfcfd" }}>
                                            <td style={{ padding: "11px 12px", borderBottom: "1px solid #f1f5f9" }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 3 }}>
                                                    <CategoryChip cat={getRequestCategory(d)} />
                                                    {d.urgent && <span style={{ padding: "1px 6px", borderRadius: 4, background: "#fee2e2", color: "#b91c1c", fontSize: 10, fontWeight: 700 }}>URGENT</span>}
                                                </div>
                                                <div style={{ fontSize: 11, color: "#94a3b8" }}>
                                                    {getRequestCode(d) && <span style={{ marginRight: 4, fontFamily: "monospace" }}>{getRequestCode(d)} ·</span>}
                                                    {t("Dépense le")} {formatDate(d.dateDepense)}
                                                </div>
                                            </td>
                                            <td style={{ padding: "11px 12px", borderBottom: "1px solid #f1f5f9", fontWeight: 700, fontSize: 14, color: "var(--admin-primary)", whiteSpace: "nowrap" }}>
                                                {formatMontant(d.montantDepense)}
                                            </td>
                                            <td style={{ padding: "11px 12px", borderBottom: "1px solid #f1f5f9", fontSize: 12, whiteSpace: "nowrap" }}>
                                                <div style={{ color: "#475569" }}>{t("Avance")} : {formatMontant(d.avance)}</div>
                                                <div style={{ marginTop: 3, fontWeight: 700, color: getSoldeAvance(d) > 0 ? "#b45309" : getSoldeAvance(d) < 0 ? "#0369a1" : "#059669" }}>
                                                    {getSoldeAvance(d) > 0 ? "+" : ""}{formatMontant(getSoldeAvance(d))}
                                                </div>
                                            </td>
                                            <td style={{ padding: "11px 12px", borderBottom: "1px solid #f1f5f9", fontSize: 12, color: "#64748b" }}>
                                                {d.devise || "MAD"}
                                            </td>
                                            <td style={{ padding: "11px 12px", borderBottom: "1px solid #f1f5f9", fontSize: 12, color: "#374151" }}>
                                                {d.ordreMissionLibelle || <span style={{ color: "#d1d5db" }}>—</span>}
                                                {(d.ordreMissionLieu || d.ordreMissionVille || d.ordreMissionAdresse) && (
                                                    <div style={{ marginTop: 3, color: "#94a3b8", fontSize: 11 }}>
                                                        {d.ordreMissionLieu || d.ordreMissionVille || d.ordreMissionAdresse}
                                                    </div>
                                                )}
                                            </td>
                                            <td style={{ padding: "11px 12px", borderBottom: "1px solid #f1f5f9" }}>
                                                <StatutBadge statut={d.status} demande={d} />
                                            </td>
                                            <td style={{ padding: "11px 12px", borderBottom: "1px solid #f1f5f9", fontSize: 12, color: "#64748b" }}>{getRequestTracking(d).label}</td>
                                            <td style={{ padding: "11px 12px", borderBottom: "1px solid #f1f5f9", textAlign: "right", display: "flex", gap: 6, justifyContent: "flex-end", alignItems: "center" }}>
                                                <button
                                                    onClick={() => { setSelectedDemande(d); setShowDetailsModal(true); }}
                                                    title={t("Détails")}
                                                    style={{
                                                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                        width: 32, height: 32, padding: 0, borderRadius: 7,
                                                        border: "1px solid #e2e8f0", background: "#fff", color: "#475569",
                                                        cursor: "pointer", transition: "all .15s"
                                                    }}
                                                    onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--admin-accent)"; e.currentTarget.style.color = "var(--admin-accent)"; e.currentTarget.style.background = "#f0f4ff"; }}
                                                    onMouseLeave={e => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.color = "#475569"; e.currentTarget.style.background = "#fff"; }}
                                                >
                                                    <Eye size={12} />
                                                </button>
                                                {canCancelRequest(d) && (
                                                    <>
                                                        <button
                                                            onClick={() => handleEditClick(d)}
                                                            title={t("Modifier la demande")}
                                                            disabled={loading}
                                                            style={{
                                                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                                width: 32, height: 32, padding: 0, borderRadius: 7,
                                                                border: "1px solid #bfdbfe", background: "#eff6ff", color: "#204385",
                                                                cursor: loading ? "not-allowed" : "pointer", transition: "all .15s",
                                                                opacity: loading ? 0.65 : 1
                                                            }}
                                                            onMouseEnter={e => { if (!loading) e.currentTarget.style.background = "#e0e7ff"; }}
                                                            onMouseLeave={e => { e.currentTarget.style.background = "#eff6ff"; }}
                                                        >
                                                            <Edit size={12} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleCancelRequest(d)}
                                                            title={t("Annuler la demande")}
                                                            disabled={loading}
                                                            style={{
                                                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                                width: 32, height: 32, padding: 0, borderRadius: 7,
                                                                border: "1px solid #fca5a5", background: "#fff7f7", color: "#dc2626",
                                                                cursor: loading ? "not-allowed" : "pointer", transition: "all .15s",
                                                                opacity: loading ? 0.65 : 1
                                                            }}
                                                            onMouseEnter={e => { if (!loading) e.currentTarget.style.background = "#fef2f2"; }}
                                                            onMouseLeave={e => { e.currentTarget.style.background = "#fff7f7"; }}
                                                        >
                                                            <XCircle size={12} />
                                                        </button>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ════════════════ MODAL NOUVELLE NOTE ════════════════ */}
            {showModal && (
                <div className="pcModal">
                    <div className="pcModal__panel pcModal__panel--wide">
                        <div className="pcModal__header">
                            <div className="pcModal__headerLeft">
                                <div className="pcModal__title">{isEditMode ? t("Modifier la note de frais") : t("Déclarer une note de frais")}</div>
                                <div className="pcModal__subtitle">{t("Sélectionnez une mission et le type de dépense")}</div>
                            </div>
                            <button className="pcIconBtn pcIconBtn--close" onClick={handleCloseModal}><X size={15} /></button>
                        </div>

                        <div className="pcModal__body">
                            <form id="ndfForm" onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 18 }}>

                                {/* Ordre de mission */}
                                <FieldBlock label={t("Ordre de mission")}>
                                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                        <div className="pcSelectWrap" style={{ flex: 1 }}>
                                            <select className="pcInput" value={form.ordreMissionId} onChange={handleOMChange}>
                                                <option value="">{t("— Aucune mission —")}</option>
                                                {myApprovedMissions.map(r => (
                                                    <option key={r.id} value={r.ordreMissionId}>
                                                        {r.ordreMissionCode} · {r.ordreMissionLibelle} ({formatDate(r.ordreMissionDateDebut)} → {formatDate(r.ordreMissionDateFin)})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        {form.ordreMissionId && (
                                            <button
                                                type="button"
                                                onClick={() => setShowMissionModal(true)}
                                                title={t("Voir les détails")}
                                                style={{ ...btn({ border: "1px solid #bae6fd", background: "#f0f9ff", color: "#0284c7", padding: "7px 10px" }) }}
                                                onMouseEnter={e => e.currentTarget.style.background = "#e0f2fe"}
                                                onMouseLeave={e => e.currentTarget.style.background = "#f0f9ff"}
                                            >
                                                <Info size={15} />
                                            </button>
                                        )}
                                    </div>
                                </FieldBlock>

                                {/* Type de frais */}
                                <FieldBlock label={<>{t("Types de frais")} <span className="pcReq">*</span></>}>
                                    <div className="pcSelectWrap">
                                        <select
                                            className="pcInput"
                                            style={{ display: isEditMode ? undefined : "none" }}
                                            multiple={!isEditMode}
                                            size={isEditMode ? 1 : Math.min(Math.max(typesDisponibles.length, 3), 6)}
                                            value={isEditMode ? (form.noteDeFraisIds[0] || "") : form.noteDeFraisIds}
                                            onChange={e => {
                                                const ids = isEditMode
                                                    ? [e.target.value]
                                                    : Array.from(e.target.selectedOptions, option => option.value);
                                                setForm(p => ({ ...p, noteDeFraisIds: ids }));
                                            }}
                                            required={isEditMode}
                                        >
                                            <option value="">{t("— Sélectionner un type de frais —")}</option>
                                            {typesDisponibles.map(expenseType => (
                                                <option key={expenseType.id} value={expenseType.id}>{t(expenseType.category, { defaultValue: expenseType.category })}{expenseType.code ? ` (${expenseType.code})` : ""}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {!isEditMode && (
                                        <div style={{ border: "1px solid #d1d5db", borderRadius: 8, overflow: "hidden", background: "#fff" }}>
                                            {typesDisponibles.map((type, index) => {
                                                const checked = form.noteDeFraisIds.includes(String(type.id));
                                                return <label key={type.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", cursor: "pointer", borderBottom: index < typesDisponibles.length - 1 ? "1px solid #eef1f5" : "none", background: checked ? "#f0f4ff" : "#fff" }}>
                                                    <input type="checkbox" checked={checked} onChange={e => setForm(p => ({
                                                        ...p,
                                                        noteDeFraisIds: e.target.checked ? [...p.noteDeFraisIds, String(type.id)] : p.noteDeFraisIds.filter(id => id !== String(type.id)),
                                                        items: e.target.checked
                                                            ? [...p.items, { noteDeFraisId: type.id, dateDepense: form.dateDepense || getMissionDateDebut(selectedOM) || "", montantDepense: "", description: "", devise: type.devise || "MAD" }]
                                                            : p.items.filter(item => String(item.noteDeFraisId) !== String(type.id)),
                                                    }))} />
                                                    <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "#374151" }}>{t(type.category, { defaultValue: type.category })}{type.code ? ` (${type.code})` : ""}</span>
                                                    {type.obligatoire && <span style={{ fontSize: 10, fontWeight: 700, color: "#dc2626" }}>{t("JUSTIFICATIF REQUIS")}</span>}
                                                </label>;
                                            })}
                                        </div>
                                    )}
                                    {!isEditMode && <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>{t("{{count}} type(s) sélectionné(s).", { count: form.noteDeFraisIds.length })}</div>}
                                    {typesDisponibles.length === 0 && selectedOM && (
                                        <div style={{ fontSize: 11, color: "#ef4444", marginTop: 4 }}>{t("Aucun type de frais autorisé pour cette mission.")}</div>
                                    )}
                                </FieldBlock>

                                {/* Récap type sélectionné */}
                                {selectedTypes.length > 0 && (
                                    <div style={{ background: "linear-gradient(135deg,#f0fdf4,#f8faff)", border: "1px solid #d1fae5", borderRadius: 10, padding: "12px 14px" }}>
                                        <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".07em", color: "#059669", marginBottom: 10 }}>{t("Règles des types sélectionnés")}</div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                            {selectedTypes.map(type => (
                                                <div key={type.id} style={{ display: "grid", gridTemplateColumns: "120px repeat(3,1fr)", gap: 10, alignItems: "center", fontSize: 12 }}>
                                                    <b>{t(type.category, { defaultValue: type.category })}</b>
                                                    <span>{t("Min.")} : {type.montantMinimum ? `${formatMontant(type.montantMinimum)} ${type.devise || "MAD"}` : t("Aucun")}</span>
                                                    <span>{t("Max.")} : {type.montantMaximum ? `${formatMontant(type.montantMaximum)} ${type.devise || "MAD"}` : t("Aucun")}</span>
                                                    <span style={{ color: type.obligatoire ? "#dc2626" : "#111827" }}>{type.obligatoire ? t("Justificatif obligatoire ({{document}})", { document: type.typeJustificatif || t("requis") }) : t("Justificatif optionnel")}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, padding: 14, border: "1px solid #dbe4ef", borderRadius: 10, background: "#f8fafc" }}>
                                    <FieldBlock label={t("Avance reçue des RH")}>
                                        <div style={{ display: "flex", gap: 8 }}>
                                            <input className="pcInput" type="number" min="0" step="0.01" value={form.avance}
                                                   onChange={e => setForm(p => ({ ...p, avance: e.target.value }))}
                                                   placeholder="0.00" />
                                            <span style={{ paddingTop: 9, fontSize: 12, fontWeight: 700 }}>{selectedType?.devise || "MAD"}</span>
                                        </div>
                                    </FieldBlock>
                                    <div style={{ padding: "8px 12px", borderRadius: 8, background: "#fff", border: "1px solid #e2e8f0" }}>
                                        <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#64748b" }}>{t("Solde de l'avance")}</div>
                                        <div style={{ marginTop: 4, fontSize: 18, fontWeight: 700, color: soldeAvance > 0 ? "#b45309" : soldeAvance < 0 ? "#0369a1" : "#059669" }}>
                                            {soldeAvance > 0 ? "+" : ""}{formatMontant(soldeAvance)} {selectedType?.devise || "MAD"}
                                        </div>
                                        <div style={{ marginTop: 3, fontSize: 11, color: "#64748b" }}>{getLibelleSolde(soldeAvance)}</div>
                                    </div>
                                </div>

                                {Number(form.avance) > 0 && (
                                    <FieldBlock label={<>{t("Justificatif de l'avance") } <span className="pcReq">*</span></>}>
                                        <input className="pcInput" type="file" onChange={e => setForm(p => ({ ...p, justificatifAvance: e.target.files?.[0] || null }))} />
                                        {form.justificatifAvance && <div style={{ fontSize: 12, color: "#475569" }}>{form.justificatifAvance.name || String(form.justificatifAvance).split("/").pop()}</div>}
                                    </FieldBlock>
                                )}

                                {!isEditMode && selectedTypes.map(type => {
                                    const item = form.items.find(entry => String(entry.noteDeFraisId) === String(type.id));
                                    if (!item) return null;
                                    return (
                                        <div key={type.id} style={{ border: "1px solid #dbe4ef", borderRadius: 10, padding: 14, display: "flex", flexDirection: "column", gap: 12 }}>
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                <CategoryChip cat={type.category} />
                                                <span style={{ fontSize: 11, color: "#64748b" }}>{type.obligatoire ? t("Justificatif requis") : t("Justificatif facultatif")}</span>
                                            </div>
                                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                                                <FieldBlock label={<>{t("Date de dépense")} <span className="pcReq">*</span></>}>
                                                    <input className="pcInput" type="date" value={item.dateDepense} min={selectedOM?.dateDebut} max={maxDate} onChange={e => updateItem(type.id, { dateDepense: e.target.value })} required />
                                                </FieldBlock>
                                                <FieldBlock label={<>{t("Montant")} <span className="pcReq">*</span></>}>
                                                    <div style={{ display: "flex", gap: 8 }}><input className="pcInput" style={{ flex: 1 }} type="number" min="0" step="0.01" value={item.montantDepense} onChange={e => updateItem(type.id, { montantDepense: e.target.value })} required /><span style={{ paddingTop: 9, fontSize: 12, fontWeight: 700 }}>{item.devise}</span></div>
                                                </FieldBlock>
                                            </div>
                                            <FieldBlock label={<>{t("Description")} <span className="pcReq">*</span></>}>
                                                <textarea className="pcInput pcInput--textarea" value={item.description} onChange={e => updateItem(type.id, { description: e.target.value })} placeholder={`Ex : ${type.category.toLowerCase()}…`} required />
                                            </FieldBlock>
                                        </div>
                                    );
                                })}

                                {isEditMode && <>
                                {/* Date + Montant */}
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                                    <FieldBlock label={<>Date de la dépense <span className="pcReq">*</span></>}>
                                        <input className="pcInput" type="date" value={form.dateDepense}
                                               onChange={e => setForm(p => ({ ...p, dateDepense: e.target.value }))}
                                               min={selectedOM?.dateDebut} max={maxDate} required />
                                    </FieldBlock>
                                    <FieldBlock label={<>Montant <span className="pcReq">*</span></>}>
                                        <div style={{ display: "flex", gap: 8 }}>
                                            <input className="pcInput" style={{ flex: 1 }} type="number" value={form.montant}
                                                   onChange={e => setForm(p => ({ ...p, montant: e.target.value }))}
                                                   min="0" step="0.01" required />
                                            <div style={{ display: "flex", alignItems: "center", padding: "0 12px", background: "#f8fafc", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 12, fontWeight: 700, color: "#64748b", whiteSpace: "nowrap" }}>
                                                {selectedType?.devise || "MAD"}
                                            </div>
                                        </div>
                                    </FieldBlock>
                                </div>

                                {/* Description */}
                                <FieldBlock label={<>Description <span className="pcReq">*</span></>}>
                                    <textarea className="pcInput pcInput--textarea" value={form.description}
                                              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                                              placeholder="Ex : Repas midi avec client X..." required />
                                </FieldBlock>
                                </>}

                                {/* Justificatifs */}
                                <FieldBlock label={<>{t("Justificatifs")}{selectedTypes.some(type => type.obligatoire) && <span className="pcReq"> *</span>}</>}>
                                    <label style={{ position: "relative", border: "1.5px dashed #cbd5e1", padding: "20px 16px", borderRadius: 10, textAlign: "center", background: "#f8fafc", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, transition: "border-color .15s" }}
                                           onMouseEnter={e => e.currentTarget.style.borderColor = "var(--admin-accent)"}
                                           onMouseLeave={e => e.currentTarget.style.borderColor = "#cbd5e1"}
                                    >
                                        <Upload size={22} color="#94a3b8" />
                                        <span style={{ fontSize: 12, color: "#64748b" }}>{t("Glissez vos fichiers ici ou")} <span style={{ color: "var(--admin-accent)", fontWeight: 600 }}>{t("parcourez")}</span></span>
                                        <input type="file" multiple onChange={e => setForm(p => ({ ...p, justificatifs: [...p.justificatifs, ...Array.from(e.target.files || [])] }))} style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer" }} />
                                    </label>
                                    {form.justificatifs.length > 0 && (
                                        <div style={{ display: "flex", flexDirection: "column", gap: 5, marginTop: 4 }}>
                                            {form.justificatifs.map((file, i) => (
                                                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f1f5f9", padding: "6px 12px", borderRadius: 7, fontSize: 12 }}>
                                                    <span style={{ color: "#374151" }}>{file.name || file}</span>
                                                    <button type="button" onClick={() => setForm(p => ({ ...p, justificatifs: p.justificatifs.filter((_, j) => j !== i) }))}
                                                            style={{ border: "none", background: "none", cursor: "pointer", padding: 2, display: "flex", alignItems: "center", color: "#ef4444" }}>
                                                        <X size={13} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </FieldBlock>

                            </form>
                        </div>

                        <div className="pcModal__footer">
                            <button type="button"
                                    onClick={handleCloseModal}
                                    style={{ ...btn({ border: "1px solid #e2e8f0", background: "#fff", color: "#374151" }) }}
                                    onMouseEnter={e => e.currentTarget.style.background = "#f3f4f6"}
                                    onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                            >
                                {t("Annuler")}
                            </button>
                            <button type="submit" form="ndfForm" disabled={loading}
                                    style={{ ...btn({ border: "none", background: "var(--admin-accent)", color: "#fff", opacity: loading ? .6 : 1, boxShadow: "0 1px 3px rgba(0,0,0,.12)" }) }}
                                    onMouseEnter={e => { if (!loading) e.currentTarget.style.filter = "brightness(1.08)"; }}
                                    onMouseLeave={e => e.currentTarget.style.filter = "brightness(1)"}
                            >
                                {loading ? <><Loader size={13} className="spin" /> {t("Envoi...")}</> : (isEditMode ? t("Enregistrer les modifications") : t("Soumettre la dépense"))}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ════════════════ MODAL DÉTAILS ════════════════ */}
            {showDetailsModal && selectedDemande && (
                <div className="pcModal">
                    <div className="pcModal__panel" style={{ width: "min(620px,100%)" }}>
                        <div className="pcModal__header">
                            <div className="pcModal__headerLeft">
                                <div className="pcModal__title">{t("Détails de la note de frais")}</div>
                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                                    <CategoryChip cat={getRequestCategory(selectedDemande)} />
                                    {getRequestCode(selectedDemande) && (
                                        <span style={{ fontSize: 11, fontFamily: "monospace", padding: "2px 7px", borderRadius: 5, background: "#f0f4ff", color: "var(--admin-accent)", border: "1px solid #dde6fb", fontWeight: 700 }}>
                                            {getRequestCode(selectedDemande)}
                                        </span>
                                    )}
                                    <StatutBadge statut={selectedDemande.status} demande={selectedDemande} />
                                </div>
                            </div>
                            <button className="pcIconBtn pcIconBtn--close" onClick={() => setShowDetailsModal(false)}><X size={15} /></button>
                        </div>

                        <div style={{ padding: "18px", overflowY: "auto", maxHeight: "calc(90vh - 130px)", display: "flex", flexDirection: "column", gap: 16 }}>

                            {selectedDemande.status === "REJECTED" && (
                                <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "12px 14px" }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: "#b91c1c", marginBottom: 8, textTransform: "uppercase", letterSpacing: ".05em" }}>
                                        <AlertCircle size={13} /> {t("Motif de rejet")}
                                    </div>
                                    <div style={{ fontSize: 13, color: "#991b1b", lineHeight: 1.5 }}>{getRejectionReason(selectedDemande) || t("Aucun motif communiqué")}</div>
                                </div>
                            )}

                            {/* Récap montant */}
                            <div style={{ background: "linear-gradient(135deg,#f0fdf4,#f8faff)", border: "1px solid #d1fae5", borderRadius: 12, padding: "14px 16px" }}>
                                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".07em", color: "#059669", marginBottom: 12 }}>{t("Informations financières")}</div>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                        <div style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".05em" }}>{t("Montant")}</div>
                                        <div style={{ fontSize: 18, fontWeight: 700, color: "#0369a1" }}>{formatMontant(selectedDemande.montantDepense)} <span style={{ fontSize: 12, fontWeight: 600, color: "#64748b" }}>{selectedDemande.devise || "MAD"}</span></div>
                                    </div>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                        <div style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".05em" }}>{t("Date dépense")}</div>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{formatDate(selectedDemande.dateDepense)}</div>
                                    </div>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                        <div style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".05em" }}>{t("Avance / solde")}</div>
                                        <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{formatMontant(selectedDemande.avance)} / <span style={{ color: getSoldeAvance(selectedDemande) > 0 ? "#b45309" : getSoldeAvance(selectedDemande) < 0 ? "#0369a1" : "#059669" }}>{getSoldeAvance(selectedDemande) > 0 ? "+" : ""}{formatMontant(getSoldeAvance(selectedDemande))}</span> {selectedDemande.devise || "MAD"}</div>
                                        <div style={{ fontSize: 11, color: "#64748b" }}>{getLibelleSolde(getSoldeAvance(selectedDemande))}</div>
                                    </div>
                                    {/*<div style={{ display: "flex", flexDirection: "column", gap: 3 }}>*/}
                                    {/*    <div style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".05em" }}>Centre de coûts</div>*/}
                                    {/*    <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{selectedDemande.centreCouts || "—"}</div>*/}
                                    {/*</div>*/}
                                </div>
                            </div>

                            {selectedDemande.items?.length > 0 && (
                                <FieldBlock label={t("Détail des frais")}>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                                        {selectedDemande.items.map(item => (
                                            <div key={item.id || item.noteDeFraisId} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 10, padding: "10px 12px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8 }}>
                                                <div><CategoryChip cat={item.category} /><div style={{ fontSize: 11, color: "#64748b", marginTop: 5 }}>{formatDate(item.dateDepense)} · {item.description || t("Sans description")}</div></div>
                                                <strong style={{ fontSize: 13 }}>{formatMontant(item.montantDepense)} {item.devise || "MAD"}</strong>
                                            </div>
                                        ))}
                                    </div>
                                </FieldBlock>
                            )}

                            {/* Mission liée */}
                            {selectedDemande.ordreMissionId && (
                                <div style={{ display: "flex", alignItems: "center", gap: 12, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 14px" }}>
                                    <Briefcase size={16} color="#64748b" style={{ flexShrink: 0 }} />
                                    <div>
                                        <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".05em" }}>{t("Ordre de mission lié")}</div>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", marginTop: 2 }}>
                                            {selectedDemande.ordreMissionCode || "—"} · {selectedDemande.ordreMissionLibelle || "—"}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {!selectedDemande.items?.length && <FieldBlock label={t("Description")}>
                                <ReadBox>{selectedDemande.description || <span style={{ color: "#cbd5e1", fontStyle: "italic" }}>{t("Non renseignée")}</span>}</ReadBox>
                            </FieldBlock>}

                            {selectedDemande.justificatifs?.length > 0 && (
                                <FieldBlock label={t("Justificatifs")}>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                        {selectedDemande.justificatifs.map((f, i) => (
                                            <div key={i} style={{ fontSize: 12, padding: "7px 12px", background: "#f1f5f9", borderRadius: 7, color: "#374151" }}>{f}</div>
                                        ))}
                                    </div>
                                </FieldBlock>
                            )}

                            {selectedDemande.justificatifAvance && (
                                <FieldBlock label={t("Justificatif de l'avance") }>
                                    <ReadBox>{selectedDemande.justificatifAvance}</ReadBox>
                                </FieldBlock>
                            )}

                        </div>

                        <div style={{ padding: "12px 18px", borderTop: "1px solid #e5e7eb", background: "#fafbfc", display: "flex", justifyContent: "flex-end" }}>
                            <button
                                onClick={() => setShowDetailsModal(false)}
                                style={{ ...btn({ border: "1px solid #e2e8f0", background: "#fff", color: "#374151" }) }}
                                onMouseEnter={e => e.currentTarget.style.background = "#f3f4f6"}
                                onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                            >
                                {t("Fermer")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ════════════════ MODAL DÉTAILS MISSION ════════════════ */}
            {showMissionModal && selectedOM && (
                <div className="pcModal">
                    <div className="pcModal__panel" style={{ width: "min(560px,100%)" }}>
                        <div className="pcModal__header">
                            <div className="pcModal__headerLeft">
                                <div className="pcModal__title">Détails de la mission</div>
                                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                                    <span style={{ fontSize: 11, fontFamily: "monospace", padding: "2px 7px", borderRadius: 5, background: "#f0f4ff", color: "var(--admin-accent)", border: "1px solid #dde6fb", fontWeight: 700 }}>{getMissionField(selectedOM, "code", "ordreMissionCode")}</span>
                                    <span style={{ fontSize: 12, color: "#64748b" }}>{getMissionField(selectedOM, "libelle", "ordreMissionLibelle")}</span>
                                </div>
                            </div>
                            <button className="pcIconBtn pcIconBtn--close" onClick={() => setShowMissionModal(false)}><X size={15} /></button>
                        </div>

                        <div style={{ padding: "18px", overflowY: "auto", maxHeight: "calc(90vh - 130px)", display: "flex", flexDirection: "column", gap: 16 }}>
                            <div style={{ background: "linear-gradient(135deg,#f0fdf4,#f8faff)", border: "1px solid #d1fae5", borderRadius: 12, padding: "14px 16px" }}>
                                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".07em", color: "#059669", marginBottom: 12 }}>Informations</div>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 12 }}>
                                    {[
                                        { k: "Date début",      v: formatDate(getMissionDateDebut(selectedOM)) },
                                        { k: "Date fin",        v: formatDate(getMissionDateFin(selectedOM)) },
                                        { k: "Lieu",            v: getMissionLieu(selectedOM) || "—" },
                                        // { k: "Centre de coût",  v: selectedOM.centreCout || "—" },
                                    ].map(({ k, v }) => (
                                        <div key={k} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <div style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".05em" }}>{k}</div>
                                            <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{v}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <FieldBlock label="Objet de la mission">
                                <ReadBox>{getMissionField(selectedOM, "objetMission", "ordreMissionObjet") || getMissionField(selectedOM, "description", "ordreMissionDescription") || <span style={{ color: "#cbd5e1", fontStyle: "italic" }}>Non renseigné</span>}</ReadBox>
                            </FieldBlock>
                        </div>

                        <div style={{ padding: "12px 18px", borderTop: "1px solid #e5e7eb", background: "#fafbfc", display: "flex", justifyContent: "flex-end" }}>
                            <button
                                onClick={() => setShowMissionModal(false)}
                                style={{ ...btn({ border: "1px solid #e2e8f0", background: "#fff", color: "#374151" }) }}
                                onMouseEnter={e => e.currentTarget.style.background = "#f3f4f6"}
                                onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExpenseManagement;
